import React, { useState, useEffect } from 'react';
import { notificationsApi, chatApi, socialApi } from '../api';
import client from '../api/client';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import HelpButton from './help/HelpButton';
import { useDesign } from '../contexts/DesignContext';
import { toast } from 'sonner';
import { haptics } from '../utils/haptics';
import { getDisplayName, getDisplayInitials } from '../lib/displayName';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import TierAvatar from './leveling/TierAvatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from './ui/sheet';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import LanguageSwitcher from './LanguageSwitcher';
import {
  Dumbbell,
  LayoutDashboard,
  Users,
  Calendar,
  Apple,
  BarChart3,
  Bell,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  ChevronRight,
  ClipboardList,
  Utensils,
  User,
  Scale,
  UserCog,
  Database,
  HardDrive,
  Code2,
  Info,
  Timer,
  Heart,
  Zap,
  Trophy,
  Flame,
  Shield,
  Target,
  Activity,
  Award,
  Crown,
  History,
  UserCircle,
  MessageCircle,
  UserPlus,
  UserCheck,
  Bot,
  Pill,
  ChevronDown,
  BookOpen,
  X,
  UtensilsCrossed,
  Calculator,
  KeyRound,
  Edit3,
  Ruler,
  Download,
  HelpCircle,
  Globe,
  Eye,
  MessageSquare,
} from 'lucide-react';

const LOGO_ICONS = { Dumbbell, Heart, Zap, Trophy, Flame, Shield, Target, Activity, Award, Crown };
import { useMobileFullscreen } from '../hooks/useMobileFullscreen';
import PushPrompt, { requestPushPrompt } from './PushPrompt';
import FeedbackDialog from './feedback/FeedbackDialog';
import GraceBanner from './GraceBanner';
import ThankYouCard from './ThankYouCard';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Lock } from 'lucide-react';
import { RestTimerProvider } from '../contexts/RestTimerContext';
import RestTimerPill from './RestTimerPill';


const WORKOUT_STORAGE_KEY = 'trainedbyrs_active_workout';

function useActiveWorkoutIndicator() {
  const [active, setActive] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [dayName, setDayName] = useState('');
  const activeRef = React.useRef(false);

  useEffect(() => {
    const check = () => {
      try {
        const raw = localStorage.getItem(WORKOUT_STORAGE_KEY);
        if (raw) {
          const data = JSON.parse(raw);
          if (data?.activeWorkout && data?.startedAt) {
            if (!activeRef.current) setActive(true);
            activeRef.current = true;
            setElapsed(Math.floor((Date.now() - data.startedAt) / 1000));
            setDayName(data.selectedDay?.day_name || 'Workout');
            return;
          }
        }
      } catch {}
      if (activeRef.current) {
        setActive(false);
        setElapsed(0);
        setDayName('');
        activeRef.current = false;
      }
    };

    check();
    let intervalId = setInterval(check, activeRef.current ? 1000 : 5000);

    // Re-check interval speed periodically
    const speedCheck = setInterval(() => {
      const shouldBeFast = activeRef.current;
      clearInterval(intervalId);
      intervalId = setInterval(check, shouldBeFast ? 1000 : 5000);
    }, 5000);

    return () => {
      clearInterval(intervalId);
      clearInterval(speedCheck);
    };
  }, []);

  const formatTime = React.useCallback((s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2,'0')}:${sec.toString().padStart(2,'0')}`;
  }, []);

  return { active, elapsed, dayName, formatTime };
}

// Flat client nav, rendered in the order the user requested.
// Dashboard is rendered separately (always first) by NavLinks below.
const clientNavTop = [
  { name: 'nav.social',       href: '/social',       icon: Heart },
  { name: 'nav.workout',      href: '/workout',      icon: Dumbbell },
  { name: 'nav.diet',         href: '/diet',         icon: Apple },
  { name: 'nav.supplements',  href: '/supplements',  icon: Pill },
  { name: 'nav.measurements', href: '/measurements', icon: Scale },
  { name: 'nav.myCoach',      href: '/my-coach',     icon: UserCheck },
  { name: 'nav.leaderboard',  href: '/leaderboard',  icon: Trophy },
  { name: 'nav.exercises',    href: '/exercises',    icon: BookOpen },
];

// Subscription is intentionally absent; it lives in the user-avatar dropdown.
const clientNav = [...clientNavTop];

const coachNav = [
  { name: 'layout.coachHub', href: '/coach-hub', icon: BarChart3 },
  { name: 'layout.coachProfile', href: '/coach-profile', icon: Shield },
  { name: 'nav.myClients', href: '/my-clients', icon: UserCog },
  { name: 'nav.questionnaires', href: '/coach-questionnaires', icon: ClipboardList },
  { name: 'nav.routines', href: '/routines', icon: ClipboardList },
  { name: 'layout.dietTemplates', href: '/diet-templates', icon: UtensilsCrossed },
  { name: 'layout.supplementTemplates', href: '/supplement-templates', icon: Pill },
  { name: 'layout.coachsToolkit', href: '/coach-calculators', icon: Calculator },
];

const adminNav = [
  { name: 'nav.users', href: '/users', icon: Users },
  { name: 'nav.aiCoach', href: '/ai-coach', icon: Bot },
  { name: 'nav.database', href: '/database', icon: Database },
  { name: 'layout.auditLog', href: '/audit-log', icon: ClipboardList },
  { name: 'nav.developer', href: '/developer', icon: Code2 },
];

// Flat lists for mobile bottom nav (still need full flat list)
const navigation = {
  admin: [...clientNav, ...coachNav, ...adminNav],
  coach: [...clientNav, ...coachNav],
  client: [...clientNav],
};

// Primary mobile tabs (5 items, no More button — swipe up to see more).
// Same set for every role: the owner/admin uses this account to log personal
// workouts/meals too, so Workout/Diet/Supplements stay in the primary slot.
// Coach-specific surfaces (My Clients, Coach Hub, etc.) are reachable from the
// hamburger menu and the auto-expanded COACH sidebar group on desktop.
const mobileClientPrimary = [
  { name: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'nav.workout', href: '/workout', icon: Dumbbell },
  { name: 'nav.diet', href: '/diet', icon: Apple },
  { name: 'nav.supplements', href: '/supplements', icon: Pill },
  { name: 'nav.social', href: '/social', icon: Heart },
];

// Set of hrefs shown in mobile primary tabs — used to filter overflow items
const mobilePrimaryHrefs = new Set(mobileClientPrimary.map(i => i.href));

const SectionBadge = ({ count }) => {
  if (!count || count <= 0) return null;
  return (
    <span
      className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary text-white text-[10px] font-bold leading-none"
      data-testid="nav-section-badge"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
};

const CollapsibleSection = React.memo(({ label, icon: SectionIcon, items, isOpen, onToggle, mobile, testId, renderNavItem, sectionBadge }) => {
  if (mobile) {
    return (
      <div className="mt-1">
        <button
          onClick={onToggle}
          className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground hover:bg-muted/50 transition-all"
          data-testid={testId}
        >
          <SectionIcon className="h-4 w-4" />
          <span className="flex-1 text-left">{label}</span>
          <SectionBadge count={sectionBadge} />
          <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {isOpen && (
          <div className="pl-2 space-y-0.5 mt-0.5" data-testid={`${testId}-items`}>
            {items.map(item => renderNavItem(item, mobile))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-1">
      <Popover open={isOpen} onOpenChange={onToggle}>
        <PopoverTrigger asChild>
          <button
            className="flex items-center gap-3 w-full px-4 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground hover:bg-muted/50 transition-all"
            data-testid={testId}
          >
            <SectionIcon className="h-4 w-4" />
            <span className="flex-1 text-left">{label}</span>
            <SectionBadge count={sectionBadge} />
            <ChevronRight className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
          </button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" sideOffset={8} className="w-52 p-2" data-testid={`${testId}-items`}>
          <div className="space-y-0.5">
            {items.map(item => renderNavItem(item, false, onToggle))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
});

const NavLinks = React.memo(({ mobile = false, coachOpen, onCoachToggle, adminOpen, onAdminToggle, role, renderNavItem, coachSectionBadge }) => {
  const { t: tNav } = useTranslation();
  const showCoach = role === 'coach' || role === 'admin';
  const showAdmin = role === 'admin';

  return (
    <nav className="space-y-0.5">
      {renderNavItem({ name: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard }, mobile)}
      {clientNavTop.map(item => renderNavItem(item, mobile))}
      {showCoach && (
        <CollapsibleSection
          label={tNav('layout.coach')}
          icon={UserCog}
          items={coachNav}
          isOpen={coachOpen}
          onToggle={onCoachToggle}
          mobile={mobile}
          testId="nav-section-coach"
          renderNavItem={renderNavItem}
          sectionBadge={coachSectionBadge}
        />
      )}
      {showAdmin && (
        <CollapsibleSection
          label={tNav('layout.admin')}
          icon={Shield}
          items={adminNav}
          isOpen={adminOpen}
          onToggle={onAdminToggle}
          mobile={mobile}
          testId="nav-section-admin"
          renderNavItem={renderNavItem}
        />
      )}
    </nav>
  );
});

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { design } = useDesign();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [followBackStatus, setFollowBackStatus] = useState({}); // { [follower_id]: true/false }
  const knownFollowBackIdsRef = React.useRef(new Set());
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [coachPendingCheckins, setCoachPendingCheckins] = useState(0);
  const [pendingQuestionnairesCount, setPendingQuestionnairesCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [moreSheetDrag, setMoreSheetDrag] = useState(null); // { startY, currentHeight, maxHeight, isDragging }
  const morePanelRef = React.useRef(null);
  // Default the COACH / ADMIN sidebar groups to OPEN for users in those roles.
  // Previously every coach landed on an app where the "COACH" group was
  // collapsed by default — Routines, Diet Plans, Coach Hub, Calculators, etc.
  // were one click deeper than Leaderboard / Exercises. New coaches discovered
  // their own tools slowly. Now: if you ARE a coach, the COACH section starts
  // expanded; same for admins.
  const isCoachOrAdminRole = user?.role === 'coach' || user?.role === 'admin';
  const isAdminRole = user?.role === 'admin';
  const [coachSectionOpen, setCoachSectionOpen] = useState(isCoachOrAdminRole);            // More sheet
  const [adminSectionOpen, setAdminSectionOpen] = useState(isAdminRole);                   // More sheet
  const [coachSectionOpenHamburger, setCoachSectionOpenHamburger] = useState(isCoachOrAdminRole); // Hamburger
  const [adminSectionOpenHamburger, setAdminSectionOpenHamburger] = useState(isAdminRole);        // Hamburger
  const [coachSectionOpenDesktop, setCoachSectionOpenDesktop] = useState(isCoachOrAdminRole);     // Desktop
  const [adminSectionOpenDesktop, setAdminSectionOpenDesktop] = useState(isAdminRole);            // Desktop
  const workoutIndicator = useActiveWorkoutIndicator();
  const { showA2HS, dismissA2HS, installPWA, deferredPrompt, isStandalone, platform } = useMobileFullscreen();
  const { isFreeUser } = useSubscription();

  // Safety cleanup: ensure body scroll is never permanently locked
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, []);

  // iOS Safari / PWA: on resume from background, the layout viewport can be
  // stale — fixed bottom nav floats above the real viewport bottom and touch
  // scroll stops registering. Nudge layout so the browser recomputes.
  useEffect(() => {
    const nudge = () => {
      if (document.visibilityState !== 'visible') return;
      window.scrollTo(window.scrollX, window.scrollY);
      document.body.style.minHeight = `${window.innerHeight}px`;
      requestAnimationFrame(() => {
        document.body.style.minHeight = '';
      });
    };
    document.addEventListener('visibilitychange', nudge);
    window.addEventListener('pageshow', nudge);
    return () => {
      document.removeEventListener('visibilitychange', nudge);
      window.removeEventListener('pageshow', nudge);
    };
  }, []);

  const pc = design.primary_color || '#FF5C00';
  const LogoIcon = LOGO_ICONS[design.logo_icon] || Dumbbell;
  const appLeft = design.app_name_left || 'trainedby';
  const appRight = design.app_name_right || 'RS';

  // Reset collapsible sections to closed every time any mobile menu opens
  useEffect(() => {
    if (moreSheetOpen) {
      setCoachSectionOpen(false);
      setAdminSectionOpen(false);
    }
  }, [moreSheetOpen]);

  useEffect(() => {
    if (mobileOpen) {
      setCoachSectionOpenHamburger(false);
      setAdminSectionOpenHamburger(false);
    }
  }, [mobileOpen]);

  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    fetchChatUnread();
    fetchPendingQuestionnaires();
    const interval = setInterval(fetchNotifications, 30000);
    const chatInterval = setInterval(fetchChatUnread, 10000);
    const questionnairesInterval = setInterval(fetchPendingQuestionnaires, 60000);
    const isCoach = user?.role === 'coach' || user?.role === 'admin';
    let coachInterval;
    if (isCoach) {
      fetchCoachPending();
      coachInterval = setInterval(fetchCoachPending, 30000);
    }
    return () => {
      clearInterval(interval);
      clearInterval(chatInterval);
      clearInterval(questionnairesInterval);
      if (coachInterval) clearInterval(coachInterval);
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear the Social-tab badge when the user navigates to /social.
  // Marks all unread `follow_new_post` notifications as read (server-side) and
  // refreshes local notifications state. Guarded by socialUnreadCount > 0 to
  // avoid no-op API calls on every Social-tab visit.
  //
  // Reads socialUnreadCount through a ref so the effect only fires on path
  // change. Without this, a follow_new_post notification arriving mid-flight
  // would re-trigger the clear on the next state update — clearing a
  // notification the user never saw.
  const socialUnreadCount = React.useMemo(
    () => notifications.filter(n => n.type === 'follow_new_post' && !n.read).length,
    [notifications]
  );
  const socialUnreadCountRef = React.useRef(0);
  useEffect(() => {
    socialUnreadCountRef.current = socialUnreadCount;
  }, [socialUnreadCount]);

  useEffect(() => {
    if (location.pathname !== '/social') return;
    if (socialUnreadCountRef.current <= 0) return;
    notificationsApi.markAllReadByType('follow_new_post')
      .then(() => fetchNotifications())
      .catch(() => {});
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchChatUnread = async () => {
    try {
      const res = await chatApi.getUnreadCount();
      setChatUnreadCount(res.data.unread_count || 0);
    } catch {}
  };

  const fetchCoachPending = async () => {
    try {
      const res = await client.get('/measurements/pending-reviews/count');
      setCoachPendingCheckins(res.data?.total || 0);
    } catch {}
  };

  const fetchPendingQuestionnaires = async () => {
    try {
      const res = await client.get('/questionnaires', { params: { status: 'active' } });
      const items = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      setPendingQuestionnairesCount(items.length);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);

      // Check follow-back status only for follower_ids we haven't checked yet
      const followerIds = Array.from(new Set(
        response.data
          .filter(n => n.type === 'new_follower' && n.metadata?.follower_id)
          .map(n => n.metadata.follower_id)
          .filter(id => !knownFollowBackIdsRef.current.has(id))
      ));
      if (followerIds.length > 0) {
        followerIds.forEach(id => knownFollowBackIdsRef.current.add(id));
        try {
          const statusRes = await client.post('/follows/batch-status', { user_ids: followerIds });
          const map = {};
          Object.entries(statusRes.data || {}).forEach(([uid, val]) => {
            map[uid] = !!val.following;
          });
          setFollowBackStatus(prev => ({ ...prev, ...map }));
        } catch {
          followerIds.forEach(id => knownFollowBackIdsRef.current.delete(id));
        }
      }
    } catch {}
  };

  const handleFollowBack = async (notif) => {
    const followerId = notif.metadata?.follower_id;
    if (!followerId) return;
    try {
      await client.post(`/follows/${followerId}`, {
        content_types: ['workout', 'meal', 'checkin'],
      });
      setFollowBackStatus(prev => ({ ...prev, [followerId]: true }));
      toast.success(t('notifications.followedBack', 'Followed back'));
      await client.put(`/notifications/${notif.id}/read`).catch(() => {});
      fetchNotifications();
      // Nudge the user to enable push notifications if they haven't yet
      requestPushPrompt();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('notifications.failedToFollowBack', 'Failed to follow back'));
    }
  };

  const handleNotificationClick = async (notif) => {
    setNotifOpen(false);

    const meta = notif.metadata || {};

    // Determine target link FIRST, then navigate immediately before any async work
    let targetLink = null;
    if (['workout_completed', 'personal_record', 'post_liked', 'post_commented', 'follow_new_post'].includes(notif.type) && meta.social_post_id) {
      targetLink = `/social?post=${meta.social_post_id}`;
    } else if (notif.type === 'client_diet' && meta.client_id) {
      targetLink = `/diet?on_behalf_of=${meta.client_id}&client_name=${encodeURIComponent(notif.title.split(' logged')[0] || 'Client')}`;
    } else if (notif.link === '/thank-you') {
      // Special: show the ThankYouCard overlay instead of navigating
      window.dispatchEvent(new CustomEvent('show-thankyou-card'));
    } else if (notif.link) {
      targetLink = notif.link;
    }

    // Navigate synchronously before async operations
    if (targetLink) navigate(targetLink);

    // Mark as read and refresh in background
    await client.put(`/notifications/${notif.id}/read`).catch(() => {});
    fetchNotifications();
  };

  const markAllAsRead = async () => {
    try {
      await client.put('/notifications/read-all');
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userNav = navigation[user?.role] || navigation.client;
  const mobilePrimary = mobileClientPrimary;
  const displayInitials = getDisplayInitials(user?.name);
  const initials = displayInitials === '?' ? 'U' : displayInitials;
  const currentYear = React.useMemo(() => new Date().getFullYear(), []);

  const renderNavItem = React.useCallback((item, mobile = false, onClose = null) => {
    const isActive = location.pathname === item.href;
    const isWorkoutNav = item.href === '/workout';
    const isChatNav = item.href === '/chat';
    const isCoachHubNav = item.href === '/coach-hub';
    const isQuestionnairesNav = item.href === '/questionnaires';
    const showIndicator = isWorkoutNav && workoutIndicator.active;
    const showChatIndicator = isChatNav && chatUnreadCount > 0;
    const showCoachHubBadge = isCoachHubNav && coachPendingCheckins > 0;
    const showQuestionnairesBadge = isQuestionnairesNav && pendingQuestionnairesCount > 0;
    const showSocialBadge = item.href === '/social' && socialUnreadCount > 0;
    const translatedName = t(item.name);
    const showLock = isFreeUser() && (item.href === '/diet' || item.href === '/supplements');
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => { if (mobile) setMobileOpen(false); if (onClose) onClose(); }}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
          isActive
            ? 'text-white shadow-lg'
            : showIndicator
              ? 'bg-green-500/10 text-green-500 border border-green-500/30'
              : showChatIndicator
                ? 'bg-blue-500/10 text-blue-500 border border-blue-500/30'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }`}
        style={isActive ? { backgroundColor: pc, boxShadow: `0 0 15px ${pc}44` } : undefined}
        data-testid={`nav-${translatedName.toLowerCase().replace(' ', '-')}`}
      >
        <div className="relative">
          <item.icon className="h-5 w-5" />
          {showIndicator && !isActive && (
            <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
          )}
          {showChatIndicator && !isActive && (
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center text-[9px] text-white font-bold">
              {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
            </span>
          )}
          {showCoachHubBadge && !isActive && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }}>
              {coachPendingCheckins > 9 ? '9+' : coachPendingCheckins}
            </span>
          )}
          {showQuestionnairesBadge && !isActive && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }}>
              {pendingQuestionnairesCount > 9 ? '9+' : pendingQuestionnairesCount}
            </span>
          )}
          {showSocialBadge && !isActive && (
            <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }} data-testid="nav-social-badge-icon">
              {socialUnreadCount > 9 ? '9+' : socialUnreadCount}
            </span>
          )}
        </div>
        <span className="flex-1">{translatedName}</span>
        {showCoachHubBadge && isActive && (
          <span className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-white/25 text-white text-[10px] font-bold" data-testid="nav-coachhub-badge">
            {coachPendingCheckins > 9 ? '9+' : coachPendingCheckins}
          </span>
        )}
        {showQuestionnairesBadge && isActive && (
          <span className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-white/25 text-white text-[10px] font-bold">
            {pendingQuestionnairesCount > 9 ? '9+' : pendingQuestionnairesCount}
          </span>
        )}
        {showSocialBadge && isActive && (
          <span className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-white/25 text-white text-[10px] font-bold" data-testid="nav-social-badge">
            {socialUnreadCount > 9 ? '9+' : socialUnreadCount}
          </span>
        )}
        {showLock && <Lock className="w-3 h-3 text-muted-foreground ml-1" />}
        {showIndicator && !isActive && (
          <span className="ml-auto flex items-center gap-1.5 text-xs font-mono text-green-500" data-testid="nav-workout-timer">
            <Timer className="h-3 w-3" />
            {workoutIndicator.formatTime(workoutIndicator.elapsed)}
          </span>
        )}
        {showChatIndicator && !isActive && (
          <span className="ml-auto text-xs font-medium text-blue-500">
            {chatUnreadCount} {t('chat.newMessage')}
          </span>
        )}
        {isActive && !showIndicator && !showChatIndicator && !showCoachHubBadge && !showQuestionnairesBadge && !showSocialBadge && <ChevronRight className="ml-auto h-4 w-4" />}
        {isActive && showIndicator && (
          <span className="ml-auto flex items-center gap-1.5 text-xs font-mono text-white/80">
            <Timer className="h-3 w-3" />
            {workoutIndicator.formatTime(workoutIndicator.elapsed)}
          </span>
        )}
      </Link>
    );
  }, [location.pathname, workoutIndicator.active, workoutIndicator.elapsed, workoutIndicator.dayName, workoutIndicator.formatTime, chatUnreadCount, coachPendingCheckins, pendingQuestionnairesCount, socialUnreadCount, isFreeUser, t, pc]);

  return (
    <RestTimerProvider>
    <>
    <GraceBanner />
    <ThankYouCard />
    <div className="min-h-screen bg-background overflow-x-clip" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden md:flex w-64 flex-col border-r border-border/50 bg-card">
        <div className="p-6 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-3">
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {appLeft}<span style={{ color: pc }}>{appRight}</span>
            </span>
          </Link>
        </div>
        <div className="flex-1 px-4 overflow-y-auto">
          <NavLinks
            coachOpen={coachSectionOpenDesktop}
            onCoachToggle={() => setCoachSectionOpenDesktop(p => !p)}
            adminOpen={adminSectionOpenDesktop}
            onAdminToggle={() => setAdminSectionOpenDesktop(p => !p)}
            role={user?.role || 'client'}
            renderNavItem={renderNavItem}
            coachSectionBadge={coachPendingCheckins}
          />
        </div>
        <div className="px-4 pb-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted transition-all"
            data-testid="sidebar-feedback-btn"
          >
            <MessageSquare className="h-5 w-5" />
            <span className="flex-1 text-left">{t('nav.feedback')}</span>
          </button>
        </div>
        <div className="p-4 border-t border-border/50 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 px-2 mb-4 w-full text-left rounded-lg hover:bg-muted/50 transition-colors py-2" data-testid="sidebar-user-menu-btn">
                <TierAvatar
                  src={user?.profile_picture}
                  alt={user?.name}
                  fallback={initials}
                  tier={user?.tier || 'rookie'}
                  level={user?.level}
                  size="h-10 w-10"
                  fallbackStyle={{ backgroundColor: `${pc}1A`, color: pc }}
                  fallbackClassName="font-bold"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium truncate">{getDisplayName(user?.name)}</p>
                    <span className="text-xs font-mono text-muted-foreground shrink-0">#{user?.id?.replace(/\D/g, '').slice(0, 4)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-64">
              <DropdownMenuLabel>
                <div>
                  <p className="font-medium">{getDisplayName(user?.name)}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/my-stats" className="flex items-center gap-2" data-testid="sidebar-profile-btn">
                  <UserCircle className="h-4 w-4" />
                  {t('nav.profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profile" className="flex items-center gap-2">
                  <Edit3 className="h-4 w-4" />
                  {t('settings.editProfile', 'Edit Profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/account-settings" className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4" />
                  {t('settings.account', 'Account')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/notification-settings" className="flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  {t('settings.notifications', 'Notifications')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/subscription" className="flex items-center gap-2" data-testid="user-dropdown-subscription">
                  <Crown className="h-4 w-4" />
                  {t('nav.subscription')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  {t('settings.allSettings', 'All Settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
                data-testid="sidebar-logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="px-2 pt-2 border-t border-border/30">
            <p className="text-xs text-muted-foreground text-center">
              {t('layout.developedBy')} <span className="font-semibold" style={{ color: pc }}>Rui Santos</span>
            </p>
            <div className="flex items-center justify-center gap-2.5 mt-1.5">
              <a href="https://www.instagram.com/ruisantos.125/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#E4405F] transition-colors" data-testid="sidebar-instagram-link">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              </a>
              <a href="https://www.facebook.com/iRSantoz/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#1877F2] transition-colors" data-testid="sidebar-facebook-link">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://wa.me/351961692558" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#25D366] transition-colors" data-testid="sidebar-whatsapp-link">
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
            </div>
            <p className="text-xs text-muted-foreground/60 text-center mt-1">
              &copy; {currentYear} {appLeft}{appRight}
            </p>
            <div className="flex items-center justify-center gap-2 mt-1">
              <Link to="/privacy-policy" className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid="sidebar-privacy-link">{t('layout.privacy')}</Link>
              <span className="text-xs text-muted-foreground/30">|</span>
              <Link to="/terms-of-service" className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid="sidebar-terms-link">{t('layout.terms')}</Link>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="md:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-40 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="flex h-16 items-center justify-between px-4 md:px-6">
            {/* Mobile: Hamburger + Logo */}
            <div className="flex items-center gap-[8px] md:hidden min-w-0">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-[48px] w-[48px] flex-shrink-0" data-testid="mobile-menu-btn" aria-label={t('nav.openMenu', 'Open menu')}>
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 flex flex-col h-full">
                  <div className="p-6 border-b border-border/50 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {appLeft}<span style={{ color: pc }}>{appRight}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 p-4 overflow-y-auto">
                    <NavLinks
                      mobile
                      coachOpen={coachSectionOpenHamburger}
                      onCoachToggle={() => setCoachSectionOpenHamburger(p => !p)}
                      adminOpen={adminSectionOpenHamburger}
                      onAdminToggle={() => setAdminSectionOpenHamburger(p => !p)}
                      role={user?.role || 'client'}
                      renderNavItem={renderNavItem}
                      coachSectionBadge={coachPendingCheckins}
                    />
                  </div>
                  <div className="px-4 pb-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => { setMobileOpen(false); setFeedbackOpen(true); }}
                      className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted transition-all"
                      data-testid="mobile-drawer-feedback-btn"
                    >
                      <MessageSquare className="h-5 w-5" />
                      <span className="flex-1 text-left">{t('nav.feedback')}</span>
                    </button>
                  </div>
                  <div className="p-4 border-t border-border/50 flex-shrink-0">
                    <Link to="/my-stats" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 px-2 mb-4 w-full text-left rounded-lg hover:bg-muted/50 transition-colors py-2">
                      <TierAvatar
                        src={user?.profile_picture}
                        alt={user?.name}
                        fallback={initials}
                        tier={user?.tier || 'rookie'}
                        level={user?.level}
                        size="h-10 w-10"
                        fallbackStyle={{ backgroundColor: `${pc}1A`, color: pc }}
                        fallbackClassName="font-bold"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium truncate">{getDisplayName(user?.name)}</p>
                          <span className="text-xs font-mono text-muted-foreground shrink-0">#{user?.id?.replace(/\D/g, '').slice(0, 4)}</span>
                        </div>
                        <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                      </div>
                    </Link>
                    <div className="px-2 pt-2 border-t border-border/30">
                      <p className="text-xs text-muted-foreground text-center">
                        {t('layout.developedBy')} <span className="font-semibold" style={{ color: pc }}>Rui Santos</span>
                      </p>
                      <div className="flex items-center justify-center gap-2.5 mt-1.5">
                        <a href="https://www.instagram.com/ruisantos.125/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#E4405F] transition-colors">
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                        </a>
                        <a href="https://www.facebook.com/iRSantoz/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#1877F2] transition-colors">
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </a>
                        <a href="https://wa.me/351961692558" target="_blank" rel="noopener noreferrer" className="text-muted-foreground/60 hover:text-[#25D366] transition-colors">
                          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        </a>
                      </div>
                      <p className="text-xs text-muted-foreground/60 text-center mt-1">
                        &copy; {currentYear} {appLeft}{appRight}
                      </p>
                      <div className="flex items-center justify-center gap-2 mt-1">
                        <Link to="/privacy-policy" onClick={() => setMobileOpen(false)} className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">{t('layout.privacy')}</Link>
                        <span className="text-xs text-muted-foreground/30">|</span>
                        <Link to="/terms-of-service" onClick={() => setMobileOpen(false)} className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors">{t('layout.terms')}</Link>
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
              {/* No wordmark in the mobile header. A 390px header cannot fit it
                  alongside the five 48px touch targets — it needs 87px and only
                  ~54px is free, so it overran the chat button by 33px. The
                  wordmark is decorative here: the hamburger drawer and the
                  desktop sidebar both still show it. */}
            </div>

            {/* Desktop: empty spacer (sidebar has the logo) */}
            <div className="hidden md:block" />

            {/* Right Side Actions */}
            {/* gap-1 (4px) on mobile made the flag, dark-mode toggle, bell and
                avatar so close together that users would tap the wrong one
                (every Playwright deep tour script consistently mis-clicked the
                flag when targeting the avatar). gap-1.5 (6px) gives 50% more
                breathing room while still fitting the 5 icons + chat into a
                390px viewport alongside the hamburger and logo. */}
            <div className="flex items-center gap-[6px] sm:gap-2 flex-shrink-0">
              <HelpButton />

              {/* Chat */}
              <Button
                variant="ghost"
                size="icon"
                className="relative h-[48px] w-[48px] sm:h-10 sm:w-10"
                onClick={() => navigate('/chat')}
                data-testid="header-chat-btn"
                tooltip={t('tooltips.chat')}
                aria-label={t('nav.chat')}
              >
                <MessageCircle className="h-5 w-5" />
                {chatUnreadCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-white text-xs bg-blue-500">
                    {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                  </Badge>
                )}
              </Button>

              {/* Language Switcher — a dropdown, not a toggle: a toggle can only
                  ever reach one "other" language, which left Spanish unreachable
                  once we shipped a third locale. */}
              <LanguageSwitcher variant="flag" />

              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="h-[48px] w-[48px] sm:h-10 sm:w-10"
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                tooltip={t('tooltips.toggleTheme')}
                aria-label={t('tooltips.toggleTheme', 'Toggle theme')}
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* Notifications */}
              <Sheet open={notifOpen} onOpenChange={(val) => { setNotifOpen(val); if (val && unreadCount > 0) markAllAsRead(); }}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-[48px] w-[48px] sm:h-10 sm:w-10" data-testid="notifications-btn" aria-label={t('nav.notifications')}>
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-white text-xs" style={{ backgroundColor: pc }}>
                        {unreadCount}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-full sm:max-w-sm p-0 flex flex-col [&>button]:hidden">
                  <SheetHeader className="px-4 pt-4 pb-3 border-b shrink-0">
                    <div className="flex items-center justify-between">
                      <SheetTitle className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {t('nav.notifications')}
                      </SheetTitle>
                      <div className="flex items-center gap-1">
                        {unreadCount > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs"
                            style={{ color: pc }}
                            onClick={markAllAsRead}
                          >
                            {t('common.markAllRead')}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 md:h-7 md:w-7"
                          onClick={() => { setNotifOpen(false); navigate('/notification-settings'); }}
                          data-testid="notification-settings-btn"
                          tooltip={t('tooltips.settings')}
                          aria-label={t('common.settings', 'Settings')}
                        >
                          <Settings className="h-4 w-4 text-muted-foreground" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 md:h-7 md:w-7"
                          onClick={() => setNotifOpen(false)}
                          tooltip={t('tooltips.close')}
                          aria-label={t('common.close', 'Close')}
                        >
                          <X className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  </SheetHeader>
                  <div className="flex-1 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-sm text-muted-foreground">
                        <Bell className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                        {t('nav.noNotifications')}
                      </div>
                    ) : (
                      notifications.map((notif) => {
                        const isNewFollower = notif.type === 'new_follower';
                        const meta = notif.metadata || {};
                        const hasDeepLink = meta.social_post_id || meta.client_id || notif.link;
                        const alreadyFollowingBack = isNewFollower && meta.follower_id && followBackStatus[meta.follower_id];

                        const NOTIF_ICONS = {
                          workout_completed: <Dumbbell className="h-4 w-4" style={{ color: pc }} />,
                          personal_record: <Trophy className="h-4 w-4 text-yellow-500" />,
                          post_liked: <Heart className="h-4 w-4 text-red-500" />,
                          post_commented: <MessageCircle className="h-4 w-4 text-blue-500" />,
                          new_follower: <UserPlus className="h-4 w-4 text-green-500" />,
                          coach_assigned: <UserCog className="h-4 w-4" style={{ color: pc }} />,
                          routine_assigned: <ClipboardList className="h-4 w-4" style={{ color: pc }} />,
                          diet_plan_assigned: <Apple className="h-4 w-4 text-green-500" />,
                          supplement_plan_assigned: <Pill className="h-4 w-4 text-primary" />,
                          client_diet: <Utensils className="h-4 w-4 text-green-500" />,
                          macros_override: <Calculator className="h-4 w-4 text-primary" />,
                          pro_granted: <Crown className="h-4 w-4 text-primary" />,
                        };

                        return (
                          <div
                            key={notif.id}
                            className={`px-4 py-3 border-b border-border/30 last:border-0 ${!notif.read ? 'bg-muted/50' : ''} ${hasDeepLink ? 'cursor-pointer hover:bg-muted/70 active:bg-muted transition-colors' : ''}`}
                            onClick={() => handleNotificationClick(notif)}
                            data-testid={`notification-${notif.id}`}
                          >
                            <div className="flex gap-3">
                              <div className="mt-0.5 shrink-0">
                                {NOTIF_ICONS[notif.type] || <Bell className="h-4 w-4 text-muted-foreground" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                {(() => {
                                  // i18n: prefer notifications.<i18n_key>.{title,body} with params.
                                  // Falls back to the legacy title/message fields for older rows.
                                  const key = notif.i18n_key || notif.type;
                                  const params = notif.params || {};
                                  const hasParams = Object.keys(params).length > 0;
                                  let titleI18n, bodyI18n;
                                  if (key && hasParams) {
                                    // New-style notification: template + params → i18next interpolates
                                    titleI18n = t(`notifications.${key}.title`, { ...params, defaultValue: notif.title || '' });
                                    bodyI18n = t(`notifications.${key}.body`, { ...params, defaultValue: notif.message || '' });
                                  } else {
                                    // Legacy notification or no params: use pre-rendered title/message
                                    titleI18n = notif.title || t('notifications.unknown.title', 'Notification');
                                    bodyI18n = notif.message || '';
                                  }
                                  return (
                                    <>
                                      <p className="text-sm font-medium leading-tight">{titleI18n}</p>
                                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{bodyI18n}</p>
                                    </>
                                  );
                                })()}
                                {notif.created_at && (
                                  <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                                    {(() => {
                                      const d = new Date(notif.created_at);
                                      const now = new Date();
                                      const diffMs = now - d;
                                      const diffMin = Math.floor(diffMs / 60000);
                                      if (diffMin < 1) return t('layout.justNow');
                                      if (diffMin < 60) return `${diffMin}m ago`;
                                      const diffH = Math.floor(diffMin / 60);
                                      if (diffH < 24) return `${diffH}h ago`;
                                      const diffD = Math.floor(diffH / 24);
                                      if (diffD < 7) return `${diffD}d ago`;
                                      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                                    })()}
                                  </p>
                                )}
                                {isNewFollower && meta.follower_id && (
                                  <div className="flex flex-wrap gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                                    {alreadyFollowingBack ? (
                                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground px-3 py-1.5">
                                        <UserCheck className="h-3.5 w-3.5" />
                                        {t('notifications.following', 'Following')}
                                      </span>
                                    ) : (
                                      <Button size="sm" className="h-10 md:h-8 text-xs px-4 text-white" style={{ backgroundColor: pc }}
                                        onClick={() => handleFollowBack(notif)} data-testid={`follow-back-${notif.id}`}>
                                        <UserPlus className="h-3.5 w-3.5 mr-1" />
                                        {t('notifications.followBack', 'Follow Back')}
                                      </Button>
                                    )}
                                  </div>
                                )}
                                {hasDeepLink && (
                                  <p className="text-xs mt-1 font-medium" style={{ color: pc }}>{t('layout.tapToView')}</p>
                                )}
                              </div>
                              {!notif.read && <div className="w-2 h-2 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: pc }} />}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </SheetContent>
              </Sheet>

              {/* User Menu — Mobile: navigate to /settings, Desktop: dropdown */}
              {/* Mobile avatar button */}
              <Button
                variant="ghost"
                className="gap-2 h-9 px-1.5 md:hidden"
                onClick={() => navigate('/settings')}
                data-testid="user-menu-btn-mobile"
              >
                <TierAvatar
                  src={user?.profile_picture}
                  alt={user?.name}
                  fallback={initials}
                  tier={user?.tier || 'rookie'}
                  level={user?.level}
                  size="h-7 w-7"
                  levelBadgeSize="h-[14px] min-w-[14px] px-[2px] text-[8px]"
                  fallbackStyle={{ backgroundColor: `${pc}1A`, color: pc }}
                  fallbackClassName="text-sm font-bold"
                />
              </Button>
              {/* Desktop dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-10 px-2 hidden md:flex" data-testid="user-menu-btn">
                    <TierAvatar
                      src={user?.profile_picture}
                      alt={user?.name}
                      fallback={initials}
                      tier={user?.tier || 'rookie'}
                      level={user?.level}
                      size="h-8 w-8"
                      levelBadgeSize="h-[15px] min-w-[15px] px-[2px] text-[9px]"
                      fallbackStyle={{ backgroundColor: `${pc}1A`, color: pc }}
                      fallbackClassName="text-sm font-bold"
                    />
                    <span className="text-sm font-medium">{getDisplayName(user?.name)}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64" sideOffset={8}>
                  <DropdownMenuLabel>
                    <div>
                      <p className="font-medium">{getDisplayName(user?.name)}</p>
                      <p className="text-xs text-muted-foreground">{user?.email}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {/* Profile */}
                  <DropdownMenuItem asChild>
                    <Link to="/my-stats" className="flex items-center gap-2" data-testid="profile-menu-btn">
                      <UserCircle className="h-4 w-4" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <Edit3 className="h-4 w-4" />
                      {t('settings.editProfile', 'Edit Profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Account */}
                  <DropdownMenuItem asChild>
                    <Link to="/account-settings" className="flex items-center gap-2">
                      <KeyRound className="h-4 w-4" />
                      {t('settings.account', 'Account')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/notification-settings" className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      {t('settings.notifications', 'Notifications')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/subscription" className="flex items-center gap-2" data-testid="user-dropdown-subscription-mobile">
                      <Crown className="h-4 w-4" />
                      {t('nav.subscription')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* Preferences */}
                  <DropdownMenuItem asChild>
                    <Link to="/workout?preferences=true" className="flex items-center gap-2">
                      <Dumbbell className="h-4 w-4" />
                      {t('settings.workouts', 'Workouts')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/privacy" className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      {t('settings.privacySocial', 'Privacy & Social')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings/units" className="flex items-center gap-2">
                      <Ruler className="h-4 w-4" />
                      {t('settings.units', 'Units')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {/* More */}
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      {t('settings.allSettings', 'All Settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive"
                    data-testid="logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8 pb-24 md:pb-8 overflow-x-clip">
          {/* Suppress the A2HS install banners on the workout page — when a
              client starts logging sets, an extra ~80px install nag stealing
              real estate above the active session is hostile. The banner re-
              appears as soon as they navigate away. */}
          {/* Native 1-click install — Chrome, Edge, Samsung Internet, Opera (fires beforeinstallprompt) */}
          {!isStandalone && deferredPrompt && !location.pathname.startsWith('/workout') && (
            <div className="mb-4 p-3 rounded-xl bg-primary/10 border border-primary/30 flex items-center gap-3 animate-in slide-in-from-top-2 cursor-pointer active:scale-[0.98] transition-transform" data-testid="a2hs-banner-native" onClick={installPWA}>
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{t('layout.installAppTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('layout.installAppDesc')}</p>
              </div>
              <div className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold flex-shrink-0" data-testid="a2hs-install-btn">
                {t('layout.install')}
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={(e) => { e.stopPropagation(); dismissA2HS(); }} className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0" data-testid="a2hs-dismiss">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.dismiss')}</TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* iOS only: Safari Add to Home Screen instructions (no native prompt available) */}
          {showA2HS && !isStandalone && platform === 'ios' && !deferredPrompt && !location.pathname.startsWith('/workout') && (
            <div className="mb-4 p-3 rounded-xl bg-primary/10 border border-primary/30 flex items-center gap-3 animate-in slide-in-from-top-2" data-testid="a2hs-banner-ios">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{t('layout.addToHomeTitle')}</p>
                <p className="text-xs text-muted-foreground">{t('layout.addToHomeDesc')}</p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={(e) => { e.stopPropagation(); dismissA2HS(); }} className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0" data-testid="a2hs-dismiss-ios" aria-label={t('common.dismiss')}>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltips.dismiss')}</TooltipContent>
              </Tooltip>
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Mobile backdrop when more menu is open */}
      <div
        className={`md:hidden fixed inset-0 z-[29] transition-opacity duration-300 ${
          moreSheetOpen || moreSheetDrag ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
        data-testid="mobile-more-backdrop"
        onClick={() => { setMoreSheetOpen(false); setMoreSheetDrag(null); }}
      >
        <div
          className="absolute inset-0 bg-black/60"
          style={{ opacity: moreSheetDrag ? Math.min(moreSheetDrag.currentHeight / moreSheetDrag.maxHeight, 1) : moreSheetOpen ? 1 : 0 }}
        />
      </div>

      <RestTimerPill />
      {/* Mobile Bottom Nav + More Options (unified container) */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-30"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Nav bar - slides up when more options open */}
        <nav
          className="bg-card border-t border-border relative z-[1]"
          data-testid="mobile-bottom-nav"
          onTouchStart={(e) => {
            const touch = e.touches[0];
            const dx0 = 0;
            // Measure the natural height of the more panel content
            const panel = morePanelRef.current;
            let maxH = window.innerHeight * 0.75;
            if (panel) {
              panel.style.maxHeight = 'none';
              panel.style.height = 'auto';
              panel.style.overflow = 'visible';
              panel.style.opacity = '1';
              maxH = Math.min(panel.scrollHeight, window.innerHeight * 0.75);
              // Reset — drag handler will set explicit height
              panel.style.maxHeight = '';
              panel.style.height = '';
              panel.style.overflow = '';
              panel.style.opacity = '';
            }
            const currentH = moreSheetOpen ? maxH : 0;
            e.currentTarget._drag = {
              startY: touch.clientY,
              startX: touch.clientX,
              startHeight: currentH,
              maxHeight: maxH,
              moved: false,
              lastY: touch.clientY,
              lastTime: Date.now(),
              velocity: 0,
            };
            // Lock body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
          }}
          onTouchMove={(e) => {
            const drag = e.currentTarget._drag;
            if (!drag) return;
            const touch = e.touches[0];
            const dx = Math.abs(touch.clientX - drag.startX);
            if (dx > 80) return; // horizontal scroll, bail
            const dy = drag.startY - touch.clientY; // positive = swiping up
            if (!drag.moved && Math.abs(dy) > 5) drag.moved = true;
            if (!drag.moved) return;
            e.preventDefault(); // prevent background scroll during drag
            // Track velocity
            const now = Date.now();
            const dt = now - drag.lastTime;
            if (dt > 0) {
              drag.velocity = (drag.lastY - touch.clientY) / dt; // px/ms, positive = up
              drag.lastY = touch.clientY;
              drag.lastTime = now;
            }
            const newHeight = Math.max(0, Math.min(drag.maxHeight, drag.startHeight + dy));
            setMoreSheetDrag({ startY: drag.startY, currentHeight: newHeight, maxHeight: drag.maxHeight, isDragging: true });
          }}
          onTouchEnd={(e) => {
            const drag = e.currentTarget._drag;
            // Unlock body scroll
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
            if (!drag || !drag.moved) { setMoreSheetDrag(null); return; }
            const finalHeight = moreSheetDrag ? moreSheetDrag.currentHeight : 0;
            const threshold = drag.maxHeight * 0.3;
            const velocityThreshold = 0.3; // px/ms
            // Snap decision: velocity wins, otherwise position threshold
            let shouldOpen;
            if (Math.abs(drag.velocity) > velocityThreshold) {
              shouldOpen = drag.velocity > 0; // positive velocity = swiping up = open
            } else {
              shouldOpen = finalHeight > threshold;
            }
            setMoreSheetDrag(null);
            setMoreSheetOpen(shouldOpen);
          }}
        >
          {/* Swipe hint line */}
          <div className="flex justify-center pt-1">
            <div className={`w-10 h-1 rounded-full transition-colors ${moreSheetOpen || moreSheetDrag ? 'bg-muted-foreground/40' : 'bg-muted-foreground/20'}`} />
          </div>
          {/* px-1, and px-0 on each tab: at 390px five tabs share 382px, which
              is just enough for the longest label ("Supplements", ~75px at the
              default size) to render without an ellipsis. */}
          <div className="flex justify-around py-2 px-1">
            {mobilePrimary.map((item) => {
              const isActive = location.pathname === item.href;
              const isWorkoutNav = item.href === '/workout';
              const isChatNav = item.href === '/chat';
              const isSocialNav = item.href === '/social';
              const showIndicator = isWorkoutNav && workoutIndicator.active;
              const showChatBadge = isChatNav && chatUnreadCount > 0;
              const showSocialBadge = isSocialNav && socialUnreadCount > 0;
              const translatedName = t(item.name);
              // max-w must be px, not rem: at the A++ text scale (125% root
              // font-size) a rem cap grows to 100px and 5 tabs overflow the
              // 390px viewport, clipping "Social" off-screen. min-w-0 lets the
              // label truncate instead of forcing the row wider.
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => haptics.selection()}
                  className={`flex flex-col items-center gap-1 py-2 px-0 flex-1 min-w-0 max-w-[80px] min-h-[48px] rounded-lg transition-all active:scale-95 active:bg-muted/50 ${
                    isActive ? '' : showIndicator ? 'text-green-500' : showChatBadge ? 'text-blue-500' : 'text-muted-foreground'
                  }`}
                  style={isActive ? { color: pc } : undefined}
                  data-testid={`mobile-nav-${translatedName.toLowerCase().replace(' ', '-')}`}
                  data-tour-anchor={
                    item.href === '/workout' ? 'nav-workout-tab'
                      : item.href === '/diet' ? 'nav-diet-tab'
                        : item.href === '/social' ? 'nav-social-tab'
                          : undefined
                  }
                >
                  <div className="relative">
                    <item.icon className="h-5 w-5" />
                    {showIndicator && (
                      <span className="absolute -top-1 -right-1.5 h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse" />
                    )}
                    {showChatBadge && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center text-[9px] text-white font-bold">
                        {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                      </span>
                    )}
                    {showSocialBadge && (
                      <span className="absolute -top-1 -right-1 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }} data-testid="mobile-nav-social-badge">
                        {socialUnreadCount > 9 ? '9+' : socialUnreadCount}
                      </span>
                    )}
                  </div>
                  {showIndicator ? (
                    <span className="text-xs font-mono font-medium text-green-500" data-testid="mobile-workout-timer">
                      {workoutIndicator.formatTime(workoutIndicator.elapsed)}
                    </span>
                  ) : (
                    <span className="text-xs font-medium truncate max-w-full">{translatedName}</span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* More Options Panel - revealed below nav when swiping up */}
        <div
          ref={morePanelRef}
          className={`bg-card overflow-hidden ${
            moreSheetDrag
              ? '' /* during drag: no transition, explicit height via style */
              : moreSheetOpen
                ? 'transition-all duration-300 ease-out max-h-[75dvh] opacity-100 border-t border-border/50'
                : 'transition-all duration-300 ease-out max-h-0 opacity-0'
          }`}
          style={moreSheetDrag ? {
            height: moreSheetDrag.currentHeight + 'px',
            opacity: Math.min(moreSheetDrag.currentHeight / Math.max(moreSheetDrag.maxHeight * 0.15, 1), 1),
            borderTop: moreSheetDrag.currentHeight > 0 ? '1px solid var(--border)' : 'none',
          } : undefined}
          data-testid="mobile-more-panel"
          onTouchStart={(e) => {
            // For swipe-down-to-close from within the panel
            const panel = morePanelRef.current;
            const scrollTop = panel ? panel.querySelector('[data-more-scroll]')?.scrollTop || 0 : 0;
            if (scrollTop > 0) return; // if scrolled inside, let native scroll handle it
            const touch = e.touches[0];
            let maxH = window.innerHeight * 0.75;
            if (panel) {
              maxH = Math.min(panel.scrollHeight, window.innerHeight * 0.75);
            }
            e.currentTarget._drag = {
              startY: touch.clientY,
              startX: touch.clientX,
              startHeight: maxH,
              maxHeight: maxH,
              moved: false,
              lastY: touch.clientY,
              lastTime: Date.now(),
              velocity: 0,
            };
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
          }}
          onTouchMove={(e) => {
            const drag = e.currentTarget._drag;
            if (!drag) return;
            const touch = e.touches[0];
            const dx = Math.abs(touch.clientX - drag.startX);
            if (dx > 80) return;
            const dy = drag.startY - touch.clientY;
            if (!drag.moved && Math.abs(dy) > 5) drag.moved = true;
            if (!drag.moved) return;
            e.preventDefault();
            const now = Date.now();
            const dt = now - drag.lastTime;
            if (dt > 0) {
              drag.velocity = (drag.lastY - touch.clientY) / dt;
              drag.lastY = touch.clientY;
              drag.lastTime = now;
            }
            const newHeight = Math.max(0, Math.min(drag.maxHeight, drag.startHeight + dy));
            setMoreSheetDrag({ startY: drag.startY, currentHeight: newHeight, maxHeight: drag.maxHeight, isDragging: true });
          }}
          onTouchEnd={(e) => {
            const drag = e.currentTarget._drag;
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
            if (!drag || !drag.moved) { setMoreSheetDrag(null); return; }
            const finalHeight = moreSheetDrag ? moreSheetDrag.currentHeight : 0;
            const threshold = drag.maxHeight * 0.3;
            const velocityThreshold = 0.3;
            let shouldOpen;
            if (Math.abs(drag.velocity) > velocityThreshold) {
              shouldOpen = drag.velocity > 0;
            } else {
              shouldOpen = finalHeight > threshold;
            }
            setMoreSheetDrag(null);
            setMoreSheetOpen(shouldOpen);
          }}
        >
          <div className="px-4 py-3 overflow-y-auto" data-more-scroll style={{ maxHeight: '75dvh' }}>
            <h3 className="text-sm font-semibold mb-3 px-1 text-muted-foreground uppercase tracking-wider">{t('nav.moreOptions')}</h3>
            {/* Bug Report / Feedback — prominent entry at the top */}
            <button
              type="button"
              onClick={() => { setMoreSheetOpen(false); setFeedbackOpen(true); }}
              className="flex items-center gap-3 w-full mb-3 px-4 py-3 rounded-xl border transition-colors"
              style={{ borderColor: `${pc}4D`, backgroundColor: `${pc}14` }}
              data-testid="mobile-more-feedback"
            >
              <MessageSquare className="h-5 w-5" style={{ color: pc }} />
              <span className="flex-1 text-left text-sm font-semibold" style={{ color: pc }}>{t('nav.feedback')}</span>
            </button>
            {/* Client-level overflow items (items not in bottom primary tabs) */}
            <div className="grid grid-cols-4 gap-1">
              {clientNavTop.filter(item => !mobilePrimaryHrefs.has(item.href)).map((item) => {
                const isActive = location.pathname === item.href;
                const translatedName = t(item.name);
                const isQuestionnaires = item.href === '/questionnaires';
                const showQuestionnairesBadge = isQuestionnaires && pendingQuestionnairesCount > 0;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMoreSheetOpen(false)}
                    className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-colors ${
                      isActive ? '' : 'text-muted-foreground hover:bg-muted'
                    }`}
                    style={isActive ? { backgroundColor: `${pc}1A`, color: pc } : undefined}
                    data-testid={`mobile-more-${translatedName.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <div className="relative">
                      <item.icon className="h-5 w-5" />
                      {showQuestionnairesBadge && (
                        <span className="absolute -top-1 -right-1.5 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }}>
                          {pendingQuestionnairesCount > 9 ? '9+' : pendingQuestionnairesCount}
                        </span>
                      )}
                    </div>
                    <span className="text-xs font-medium text-center leading-tight">{translatedName}</span>
                  </Link>
                );
              })}
            </div>
            {/* Coach section */}
            {(user?.role === 'coach' || user?.role === 'admin') && (
              <div className="mt-3">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                  <UserCog className="h-3.5 w-3.5" />
                  <span>{t('layout.coach')}</span>
                  {coachPendingCheckins > 0 && (
                    <span className="inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary text-white text-[10px] font-bold leading-none">
                      {coachPendingCheckins > 9 ? '9+' : coachPendingCheckins}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1" data-testid="mobile-more-coach-items">
                  {coachNav.map((item) => {
                    const isActive = location.pathname === item.href;
                    const translatedName = t(item.name);
                    const isHubItem = item.href === '/coach-hub';
                    const showHubBadge = isHubItem && coachPendingCheckins > 0;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMoreSheetOpen(false)}
                        className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-colors ${
                          isActive ? '' : 'text-muted-foreground hover:bg-muted'
                        }`}
                        style={isActive ? { backgroundColor: `${pc}1A`, color: pc } : undefined}
                        data-testid={`mobile-more-${translatedName.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <div className="relative">
                          <item.icon className="h-5 w-5" />
                          {showHubBadge && (
                            <span className="absolute -top-1 -right-1.5 h-4 min-w-[16px] px-0.5 rounded-full flex items-center justify-center text-[9px] text-white font-bold" style={{ backgroundColor: pc }}>
                              {coachPendingCheckins > 9 ? '9+' : coachPendingCheckins}
                            </span>
                          )}
                        </div>
                        <span className="text-xs font-medium text-center leading-tight">{translatedName}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Admin section */}
            {user?.role === 'admin' && (
              <div className="mt-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70">
                  <Shield className="h-3.5 w-3.5" />
                  <span>{t('layout.admin')}</span>
                </div>
                <div className="grid grid-cols-4 gap-1 mt-1" data-testid="mobile-more-admin-items">
                  {adminNav.map((item) => {
                    const isActive = location.pathname === item.href;
                    const translatedName = t(item.name);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMoreSheetOpen(false)}
                        className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-colors ${
                          isActive ? '' : 'text-muted-foreground hover:bg-muted'
                        }`}
                        style={isActive ? { backgroundColor: `${pc}1A`, color: pc } : undefined}
                        data-testid={`mobile-more-${translatedName.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <item.icon className="h-5 w-5" />
                        <span className="text-xs font-medium text-center leading-tight">{translatedName}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Account & Settings section */}
            <div className="mt-3 pt-2 border-t border-border/30">
              <p className="text-xs font-semibold mb-2 px-1 text-muted-foreground uppercase tracking-wider">{t('settings.account', 'Account')}</p>
              <div className="grid grid-cols-4 gap-1">
                {[
                  { name: 'nav.profile', href: '/my-stats', icon: UserCircle },
                  { name: 'settings.editProfile', href: '/profile', icon: Edit3 },
                  { name: 'settings.account', href: '/account-settings', icon: KeyRound },
                  { name: 'settings.notifications', href: '/notification-settings', icon: Bell },
                  { name: 'settings.allSettings', href: '/settings', icon: Settings },
                  { name: 'nav.appInfo', href: '/app-info', icon: Info },
                ].map((item) => {
                  const isActive = location.pathname === item.href;
                  const translatedName = t(item.name, item.name.split('.').pop());
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMoreSheetOpen(false)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-colors ${
                        isActive ? '' : 'text-muted-foreground hover:bg-muted'
                      }`}
                      style={isActive ? { backgroundColor: `${pc}1A`, color: pc } : undefined}
                      data-testid={`mobile-more-${translatedName.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="text-xs font-medium text-center leading-tight">{translatedName}</span>
                    </Link>
                  );
                })}
                <button
                  onClick={() => { setMoreSheetOpen(false); handleLogout(); }}
                  className="flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl transition-colors text-destructive hover:bg-destructive/10"
                  data-testid="mobile-more-logout"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="text-xs font-medium text-center leading-tight">{t('common.logout')}</span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-4 pt-3 border-t border-border/30">
              <Link to="/privacy-policy" onClick={() => setMoreSheetOpen(false)} className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors" data-testid="mobile-more-privacy-link">{t('layout.privacyPolicy')}</Link>
              <span className="text-xs text-muted-foreground/30">|</span>
              <Link to="/terms-of-service" onClick={() => setMoreSheetOpen(false)} className="text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors" data-testid="mobile-more-terms-link">{t('layout.termsOfService')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Push notification auto-prompt for mobile */}
    <PushPrompt />
    <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
    </RestTimerProvider>
  );
}
